import { useState, useEffect } from "react";
import { Search, Menu, X, Heart, User, Filter, ChevronDown, Globe, ScanBarcode, Calendar, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Cart from "./Cart";
import Wishlist from "./Wishlist";
import SearchModal from "./SearchModal";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useLogoSettings } from "@/hooks/useLogoSettings";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFeatureSettings } from "@/hooks/useFeatureSettings";
import { useSpecialist } from "@/hooks/useSpecialist";
import { BarcodeScanner } from "./BarcodeScanner";
import { AISkinConsultant } from "./AISkinConsultant";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface NavItem {
  name: string;
  category?: string;
  href?: string;
}

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isAIConsultantOpen, setIsAIConsultantOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const { data: features } = useFeatureSettings();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedSkinType, setSelectedSkinType] = useState("all");
  const [priceRange, setPriceRange] = useState([0, 500]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isSpecialist } = useSpecialist();
  const { logoUrl, loading: logoLoading } = useLogoSettings();
  const { siteTitle, loading: titleLoading } = useSiteSettings();

  // Cached query for menu categories - revalidate every 30 minutes
  const { data: categories = [] } = useQuery({
    queryKey: ['menu-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categories")
        .select("id, name, name_en, slug")
        .eq("is_active", true)
        .eq("show_in_header", true)
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return data || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  // Cached query for skin types - revalidate every 30 minutes
  const { data: skinTypesData = [] } = useQuery({
    queryKey: ['skin-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("skin_types")
        .select("name, name_en")
        .order("name");

      if (error) throw error;
      return data || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  const allCategories = categories.map((c) => c.name);
  const skinTypes = skinTypesData;

  // Helper function to get translated category name
  const getCategoryName = (category: { name: string; name_en?: string }) => {
    return language === 'en' && category.name_en ? category.name_en : category.name;
  };

  // Helper function to get translated skin type name
  const getSkinTypeName = (skinType: { name: string; name_en?: string }) => {
    return language === 'en' && skinType.name_en ? skinType.name_en : skinType.name;
  };

  // Sync with location state when navigating
  useEffect(() => {
    if (location.state?.filterCategory) {
      setSelectedCategory(location.state.filterCategory);
    }
    if (location.state?.filterSkinType) {
      setSelectedSkinType(location.state.filterSkinType);
    }
    if (location.state?.filterPriceRange) {
      setPriceRange(location.state.filterPriceRange);
    }
  }, [location.state]);


  // Create navigation items with loaded categories plus static items
  const navItems: NavItem[] = [
    ...categories.map((category) => ({
      name: getCategoryName(category),
      category: category.name,
    })),
    ...(features?.booking_enabled ? [{ name: language === 'en' ? 'Booking' : 'Rezervacija', href: "/booking" }] : []),
    ...(isSpecialist ? [{ name: language === 'en' ? 'My Calendar' : 'Mano kalendorius', href: "/specialist" }] : []),
  ];

  const handleCategoryClick = (category: string) => {
    // If we're already on the home page, just scroll to products
    if (location.pathname === "/") {
      // Navigate to update the state for filtering
      navigate("/", { state: { filterCategory: category } });
      // Scroll to products section after a short delay to ensure state is updated
      setTimeout(() => {
        const productsSection = document.getElementById("products");
        if (productsSection) {
          productsSection.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } else {
      // Navigate to home page with category filter
      navigate("/", { state: { filterCategory: category } });
    }
  };

  const handleLogoClick = () => {
    if (location.pathname === "/") {
      // If already on home page, scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // If on another page, navigate to home
      navigate("/");
    }
  };

  // Handle browser back button to close mobile menu
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (isMenuOpen) {
        event.preventDefault();
        setIsMenuOpen(false);
        // Push the state back to stay on the same page
        window.history.pushState(null, '', window.location.href);
      }
    };

    if (isMenuOpen) {
      window.history.pushState({ menuOpen: true }, '');
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isMenuOpen]);

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center space-x-4">
            <button 
              className="lg:hidden" 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? t.header.closeMenu : t.header.openMenu}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <div className="flex items-center gap-3 cursor-pointer" onClick={handleLogoClick}>
              {logoUrl && <img src={logoUrl} alt={siteTitle} className="h-10 object-contain" width={120} height={40} />}
              <h1 className="text-2xl font-bold bg-accent-gradient bg-clip-text text-transparent">{siteTitle}</h1>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) =>
              item.href ? (
                <button
                  key={item.name}
                  onClick={() => navigate(item.href!)}
                  className="text-foreground hover:text-primary transition-colors duration-200 font-medium"
                  aria-label={`${t.header.goTo} ${item.name}`}
                >
                  {item.name}
                </button>
              ) : (
                <button
                  key={item.name}
                  onClick={() => handleCategoryClick(item.category!)}
                  className="text-foreground hover:text-primary transition-colors duration-200 font-medium"
                  aria-label={`${t.header.show} ${item.name} ${t.header.products}`}
                >
                  {item.name}
                </button>
              ),
            )}
          </nav>

          {/* Actions */}
          <div className="flex items-center space-x-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Change language">
                  <Globe size={20} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLanguage('lt')} className={language === 'lt' ? 'bg-accent' : ''}>
                  Lietuvių
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('en')} className={language === 'en' ? 'bg-accent' : ''}>
                  English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Scanner button - visible on all devices */}
            {features?.barcode_scanner_enabled && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setIsScannerOpen(true)}
                aria-label={language === 'en' ? 'Scan barcode' : 'Skenuoti kodą'}
              >
                <ScanBarcode size={20} />
              </Button>
            )}
            
            {/* AI Skin Consultant button - visible on all devices */}
            {features?.ai_skin_consultant_enabled && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setIsAIConsultantOpen(true)}
                aria-label={language === 'en' ? 'AI Skin Consultant' : 'AI Odos Konsultantė'}
                className="relative"
              >
                <Sparkles size={20} />
              </Button>
            )}
            
            {/* Desktop-only action buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              <SearchModal>
                <Button variant="ghost" size="icon" aria-label={t.header.search}>
                  <Search size={20} />
                </Button>
              </SearchModal>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(user ? "/account" : "/auth")}
                aria-label={user ? t.header.myAccount : t.header.login}
              >
                <User size={20} />
              </Button>
              <Wishlist />
              <Cart />
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="lg:hidden border-t border-border bg-background">
            <ScrollArea className="h-[calc(100vh-5rem)] px-4 py-4">
              {navItems.map((item) =>
                item.href ? (
                  <button
                    key={item.name}
                    onClick={() => {
                      navigate(item.href!);
                      setIsMenuOpen(false);
                    }}
                    className="block w-full text-left py-2 text-foreground hover:text-primary transition-colors duration-200"
                    aria-label={`${t.header.goTo} ${item.name}`}
                  >
                    {item.name}
                  </button>
                ) : (
                  <button
                    key={item.name}
                    onClick={() => {
                      handleCategoryClick(item.category!);
                      setIsMenuOpen(false);
                    }}
                    className="block w-full text-left py-2 text-foreground hover:text-primary transition-colors duration-200"
                    aria-label={`${t.header.show} ${item.name} ${t.header.products}`}
                  >
                    {item.name}
                  </button>
                ),
              )}
              <div className="mt-4 pt-4 border-t border-border">
                <SearchModal>
                  <Button variant="ghost" className="w-full justify-start" aria-label={t.header.search}>
                    <Search size={20} className="mr-2" />
                    {t.header.search}
                  </Button>
                </SearchModal>
                {features?.barcode_scanner_enabled && (
                  <Button 
                    variant="ghost" 
                    className="w-full justify-start mt-2"
                    onClick={() => {
                      setIsScannerOpen(true);
                      setIsMenuOpen(false);
                    }}
                    aria-label={language === 'en' ? 'Scan barcode' : 'Skenuoti kodą'}
                  >
                    <ScanBarcode size={20} className="mr-2" />
                    {language === 'en' ? 'Scan Barcode' : 'Skenuoti kodą'}
                  </Button>
                )}
                {features?.ai_skin_consultant_enabled && (
                  <Button 
                    variant="ghost" 
                    className="w-full justify-start mt-2"
                    onClick={() => {
                      setIsAIConsultantOpen(true);
                      setIsMenuOpen(false);
                    }}
                    aria-label={language === 'en' ? 'AI Skin Consultant' : 'AI Odos Konsultantė'}
                  >
                    <Sparkles size={20} className="mr-2" />
                    {language === 'en' ? 'AI Skin Consultant' : 'AI Odos Konsultantė'}
                  </Button>
                )}
              </div>

              {/* Product Filters Section */}
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center gap-2 mb-3">
                  <Filter className="h-4 w-4 text-primary" />
                  <h3 className="font-medium text-foreground">{t.header.productFilters}</h3>
                  {(selectedCategory !== "all" || selectedSkinType !== "all") && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedCategory("all");
                        setSelectedSkinType("all");
                      }}
                      className="ml-auto text-xs text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3 w-3 mr-1" />
                      {t.header.clearFilters}
                    </Button>
                  )}
                </div>

                <div className="space-y-4">
                  {/* Category Filter */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">{t.header.category}</label>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger className="w-full bg-background border border-border" aria-label={t.header.selectCategory}>
                        <SelectValue placeholder={t.header.selectCategory}>
                          {selectedCategory === "all" ? t.header.allProducts : 
                            categories.find(c => c.name === selectedCategory) 
                              ? getCategoryName(categories.find(c => c.name === selectedCategory)!)
                              : selectedCategory
                          }
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="bg-background border border-border z-50">
                        <SelectItem value="all">{t.header.allProducts}</SelectItem>
                        {categories.map((category) => (
                          <SelectItem key={category.name} value={category.name}>
                            {getCategoryName(category)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Skin Type Filter */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">{t.header.skinType}</label>
                    <Select value={selectedSkinType} onValueChange={setSelectedSkinType}>
                      <SelectTrigger className="w-full bg-background border border-border" aria-label={t.header.selectSkinType}>
                        <SelectValue placeholder={t.header.selectSkinType}>
                          {selectedSkinType === "all" ? t.header.all : 
                            skinTypes.find(st => st.name === selectedSkinType) 
                              ? getSkinTypeName(skinTypes.find(st => st.name === selectedSkinType)!)
                              : selectedSkinType
                          }
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="bg-background border border-border z-50">
                        <SelectItem value="all">{t.header.all}</SelectItem>
                        {skinTypes.map((skinType) => (
                          <SelectItem key={skinType.name} value={skinType.name}>
                            {getSkinTypeName(skinType)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Price Range Filter */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">{t.header.priceRange}</label>
                    <div className="px-3">
                      <Slider
                        max={500}
                        min={0}
                        step={5}
                        value={priceRange}
                        onValueChange={(value) => setPriceRange(value as [number, number])}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>€{priceRange[0]}</span>
                        <span>€{priceRange[1]}</span>
                      </div>
                    </div>
                  </div>

                  {/* Apply Filters Button */}
                  <Button
                    className="w-full"
                    onClick={() => {
                      navigate("/", {
                        state: {
                          filterCategory: selectedCategory === "all" ? "" : selectedCategory,
                          filterSkinType: selectedSkinType === "all" ? "" : selectedSkinType,
                          filterPriceRange: priceRange,
                        },
                      });
                      setIsMenuOpen(false);
                    }}
                  >
                    {t.header.applyFilters}
                  </Button>
                </div>
              </div>
            </ScrollArea>
          </nav>
        )}
      </div>
      
      {/* Barcode Scanner Dialog */}
      <BarcodeScanner 
        open={isScannerOpen} 
        onClose={() => setIsScannerOpen(false)} 
      />
      
      {/* AI Skin Consultant Dialog */}
      <AISkinConsultant
        open={isAIConsultantOpen}
        onClose={() => setIsAIConsultantOpen(false)}
      />
    </header>
  );
};

export default Header;
